import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { 
  FaBoxes, 
  FaBarcode, 
  FaCalculator, 
  FaExchangeAlt, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaCamera, 
  FaSearch, 
  FaHistory, 
  FaCheckCircle 
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

interface CompanyType {
  id: number;
  company_name: string;
  name?: string;
}

interface PackagingCategoryType {
  id: number;
  name: string;
  company_id: number;
}

interface InventoryItemType {
  id: number;
  sku: string;
  name: string;
  description: string;
  price: number;
  minimum_stock_level: number;
  current_stock?: number;
  packaging_category_id?: number | null;
  packaging_category_name?: string | null;
  units_per_package?: number;
  package_unit?: string;
  sku_abbreviation?: string | null;
  sku_type?: string | null;
  barcode?: string | null;
  batch_code?: string | null;
}

interface WarehouseType {
  id: number;
  name: string;
  location: string;
}

interface ValuationItemType {
  id: number;
  sku: string;
  name: string;
  total_quantity: number;
  total_value: number;
  average_cost: number;
}

function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const InventoryDashboard: React.FC = () => {
  const [role, setRole] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  
  // Tabs
  const [activeTab, setActiveTab] = useState<'items' | 'adjustments' | 'valuation' | 'alerts' | 'scan'>('items');
  
  // Lists
  const [items, setItems] = useState<InventoryItemType[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [lowStockItems, setLowStockItems] = useState<any[]>([]);
  const [valuationData, setValuationData] = useState<ValuationItemType[]>([]);
  const [categories, setCategories] = useState<PackagingCategoryType[]>([]);
  
  // Item Form
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItemType | null>(null);
  const [itemForm, setItemForm] = useState({
    sku: '',
    name: '',
    description: '',
    price: '',
    minimum_stock_level: '5',
    packaging_category_id: '',
    units_per_package: '1',
    package_unit: 'pcs',
    sku_abbreviation: '',
    sku_type: 'Standard',
    barcode: '',
    batch_code: ''
  });

  // Quick category creation
  const [showQuickCategory, setShowQuickCategory] = useState(false);
  const [quickCategoryName, setQuickCategoryName] = useState('');
  
  // Adjustment Form
  const [adjustForm, setAdjustForm] = useState({
    itemId: '',
    warehouseId: '',
    targetWarehouseId: '',
    quantity: '',
    type: 'IN', // IN, OUT, TRANSFER
    unitCost: '',
    batchNumber: '',
    remarks: ''
  });
  
  // Feedback
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Barcode / Label Printing state
  const [barcodeModalItem, setBarcodeModalItem] = useState<InventoryItemType | null>(null);
  const [labelQuantity, setLabelQuantity] = useState<number>(4);
  const [customBatch, setCustomBatch] = useState<string>('');
  
  // Scanning State
  const [scanSku, setScanSku] = useState('');
  const [scannedItem, setScannedItem] = useState<InventoryItemType | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scanAdjustForm, setScanAdjustForm] = useState({
    type: 'IN', // IN or OUT
    quantity: '',
    warehouseId: '',
    batchNumber: '',
    remarks: ''
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Load companies & roles
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = decodeToken(token);
      setRole(decoded.role || null);
      
      const companyId = decoded.userId || decoded.id || decoded.company_id;
      const activeWorkspaceId = localStorage.getItem("selected_company_id");
      if (decoded.role === 'company' && companyId) {
        setSelectedCompanyId(String(companyId));
      } else if (activeWorkspaceId && activeWorkspaceId !== "all") {
        setSelectedCompanyId(String(activeWorkspaceId));
      }
    } else {
      const activeWorkspaceId = localStorage.getItem("selected_company_id");
      if (activeWorkspaceId && activeWorkspaceId !== "all") {
        setSelectedCompanyId(String(activeWorkspaceId));
      }
    }
    
    const activeWorkspaceId = localStorage.getItem("selected_company_id");
    axios.get(`${API_URL}/auth/companies`)
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : [];
        if (activeWorkspaceId && activeWorkspaceId !== "all") {
          setCompanies(all.filter((c: any) => String(c.id) === String(activeWorkspaceId)));
          setSelectedCompanyId(String(activeWorkspaceId));
        } else {
          setCompanies(all);
        }
      })
      .catch(err => console.error('Error fetching companies:', err));
  }, []);

  // Trigger loads when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      fetchItems();
      fetchWarehouses();
      fetchLowStockReport();
      fetchValuationReport();
      fetchCategories();
    }
  }, [selectedCompanyId]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/inventory/packaging-categories?company_id=${selectedCompanyId}`);
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to load packaging categories:', err);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/auth/inventory/items?company_id=${selectedCompanyId}`);
      setItems(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/inventory/warehouses?company_id=${selectedCompanyId}`);
      setWarehouses(res.data);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  const fetchLowStockReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/inventory/low-stock-report?company_id=${selectedCompanyId}`);
      setLowStockItems(res.data);
    } catch (err) {
      console.error('Failed to load low stock alerts:', err);
    }
  };

  const fetchValuationReport = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/inventory/valuation?company_id=${selectedCompanyId}`);
      const rawItems = res.data && res.data.items ? res.data.items : [];
      const mapped: ValuationItemType[] = rawItems.map((item: any) => ({
        id: item.id,
        sku: item.sku,
        name: item.name,
        total_quantity: parseFloat(item.total_stock) || 0,
        total_value: parseFloat(item.valuation) || 0,
        average_cost: parseFloat(item.avg_unit_cost) || 0
      }));
      setValuationData(mapped);
    } catch (err) {
      console.error('Failed to load valuation report:', err);
    }
  };

  // Item form submit
  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      let generatedBarcode = itemForm.barcode;
      if (!generatedBarcode || !generatedBarcode.trim()) {
        const catId = itemForm.packaging_category_id;
        const matchedCat = categories.find(c => String(c.id) === String(catId));
        const catAbbr = matchedCat 
          ? matchedCat.name.replace(/[^a-zA-Z0-9]/g, "").slice(0, 3).toUpperCase() 
          : "STD";
        const randSeq = Math.floor(1000000000 + Math.random() * 9000000000);
        generatedBarcode = `${catAbbr}${randSeq}`;
      }

      const payload = {
        company_id: Number(selectedCompanyId),
        sku: itemForm.sku,
        name: itemForm.name,
        description: itemForm.description,
        price: parseFloat(itemForm.price) || 0,
        minimum_stock_level: parseFloat(itemForm.minimum_stock_level) || 0,
        packaging_category_id: itemForm.packaging_category_id ? Number(itemForm.packaging_category_id) : null,
        units_per_package: parseInt(itemForm.units_per_package) || 1,
        package_unit: itemForm.package_unit || 'pcs',
        sku_abbreviation: itemForm.sku_abbreviation || null,
        sku_type: itemForm.sku_type || null,
        barcode: generatedBarcode,
        batch_code: itemForm.batch_code || null
      };

      if (editingItem) {
        await axios.put(`${API_URL}/auth/inventory/item/${editingItem.id}`, payload);
        setSuccess('Item updated successfully!');
      } else {
        await axios.post(`${API_URL}/auth/inventory/item`, payload);
        setSuccess('Item created successfully!');
      }
      
      setShowItemModal(false);
      setEditingItem(null);
      setItemForm({
        sku: '',
        name: '',
        description: '',
        price: '',
        minimum_stock_level: '5',
        packaging_category_id: '',
        units_per_package: '1',
        package_unit: 'pcs',
        sku_abbreviation: '',
        sku_type: 'Standard',
        barcode: '',
        batch_code: ''
      });
      fetchItems();
      fetchLowStockReport();
      fetchValuationReport();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleEditClick = (item: InventoryItemType) => {
    setEditingItem(item);
    setItemForm({
      sku: item.sku,
      name: item.name,
      description: item.description,
      price: String(item.price),
      minimum_stock_level: String(item.minimum_stock_level),
      packaging_category_id: String(item.packaging_category_id || ''),
      units_per_package: String(item.units_per_package || '1'),
      package_unit: item.package_unit || 'pcs',
      sku_abbreviation: item.sku_abbreviation || '',
      sku_type: item.sku_type || 'Standard',
      barcode: item.barcode || '',
      batch_code: item.batch_code || ''
    });
    setShowItemModal(true);
  };

  const handlePrintLabels = () => {
    const printContent = document.getElementById('printable-labels-area');
    if (!printContent) return;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Barcode Labels</title>
            <style>
              @page {
                size: auto;
                margin: 0mm;
              }
              body {
                font-family: monospace;
                margin: 10px;
                padding: 0;
                display: flex;
                flex-wrap: wrap;
                gap: 15px;
                justify-content: center;
              }
              .label-card {
                width: 220px;
                height: 110px;
                border: 1px dashed #ccc;
                border-radius: 6px;
                padding: 8px;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                background: white;
                page-break-inside: avoid;
              }
              .label-header {
                font-size: 8px;
                font-weight: bold;
                color: #555;
                text-transform: uppercase;
                display: flex;
                justify-content: space-between;
              }
              .label-title {
                font-size: 10px;
                font-weight: bold;
                color: #000;
                margin: 2px 0;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .barcode-sim {
                height: 30px;
                display: flex;
                align-items: flex-end;
                justify-content: center;
                gap: 1.5px;
                margin: 3px 0;
              }
              .barcode-bar {
                background: black;
                height: 100%;
              }
              .barcode-text {
                font-size: 9px;
                text-align: center;
                letter-spacing: 2px;
                font-weight: bold;
              }
              .label-footer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 8px;
                font-weight: bold;
                border-top: 0.5px solid #eee;
                padding-top: 2px;
              }
              @media print {
                body {
                  margin: 0;
                }
                .label-card {
                  border: 1px solid #000;
                }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() { window.close(); }, 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleDeleteItem = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/auth/inventory/item/${id}`);
      setSuccess('Item deleted successfully!');
      fetchItems();
      fetchLowStockReport();
      fetchValuationReport();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleQuickCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickCategoryName.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/auth/inventory/packaging-categories`, {
        name: quickCategoryName.trim(),
        company_id: Number(selectedCompanyId)
      });
      setSuccess('Packaging category created successfully!');
      setQuickCategoryName('');
      setShowQuickCategory(false);
      await fetchCategories();
      setItemForm(prev => ({
        ...prev,
        packaging_category_id: String(res.data.id)
      }));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this packaging category?')) return;
    try {
      await axios.delete(`${API_URL}/auth/inventory/packaging-categories/${id}`);
      setSuccess('Packaging category deleted!');
      fetchCategories();
      fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  // Adjustment Submit
  const handleAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const isTransfer = adjustForm.type === 'TRANSFER';
      const payload = {
        item_id: Number(adjustForm.itemId),
        warehouse_id: Number(adjustForm.warehouseId),
        target_warehouse_id: isTransfer ? Number(adjustForm.targetWarehouseId) : undefined,
        quantity: parseFloat(adjustForm.quantity) * (adjustForm.type === 'OUT' ? -1 : 1),
        unit_cost: adjustForm.unitCost ? parseFloat(adjustForm.unitCost) : undefined,
        batch_number: adjustForm.batchNumber || undefined,
        remarks: adjustForm.remarks
      };

      if (isTransfer) {
        await axios.post(`${API_URL}/auth/inventory/transfer`, {
          item_id: payload.item_id,
          from_warehouse_id: payload.warehouse_id,
          to_warehouse_id: payload.target_warehouse_id,
          quantity: parseFloat(adjustForm.quantity),
          remarks: payload.remarks
        });
        setSuccess('Inventory transferred successfully!');
      } else {
        await axios.post(`${API_URL}/auth/inventory/adjust`, payload);
        setSuccess('Stock ledger updated successfully!');
      }

      setAdjustForm({
        itemId: '',
        warehouseId: '',
        targetWarehouseId: '',
        quantity: '',
        type: 'IN',
        unitCost: '',
        batchNumber: '',
        remarks: ''
      });
      fetchItems();
      fetchLowStockReport();
      fetchValuationReport();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process stock adjustment');
    }
  };

  const handleScanAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scannedItem) return;
    setError('');
    setSuccess('');
    try {
      const payload = {
        item_id: Number(scannedItem.id),
        warehouse_id: Number(scanAdjustForm.warehouseId),
        quantity: parseFloat(scanAdjustForm.quantity) * (scanAdjustForm.type === 'OUT' ? -1 : 1),
        batch_number: scanAdjustForm.batchNumber || undefined,
        remarks: scanAdjustForm.remarks
      };

      await axios.post(`${API_URL}/auth/inventory/adjust`, payload);
      setSuccess('Stock ledger updated via barcode scanner successfully!');
      
      // Refresh inventory
      await fetchItems();
      await fetchLowStockReport();
      await fetchValuationReport();

      setScanAdjustForm({
        type: 'IN',
        quantity: '',
        warehouseId: '',
        batchNumber: '',
        remarks: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit barcode adjustment');
    }
  };

  // Camera Scanning Simulation / Actual Camera Stream
  const startCamera = async () => {
    setError('');
    try {
      setCameraActive(true);
      const constraints = { video: { facingMode: 'environment' } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.warn('Camera stream blocked or unavailable, using premium mockup mode.');
    }
  };

  const stopCamera = () => {
    setCameraActive(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const simulateScan = () => {
    setError('');
    if (items.length === 0) {
      setError('No inventory items exist to simulate scanning.');
      return;
    }
    const randomIndex = Math.floor(Math.random() * items.length);
    const scanned = items[randomIndex];
    setScanSku(scanned.sku);
    setScannedItem(scanned);
    setSuccess(`Scanned SKU: ${scanned.sku} (${scanned.name})`);
  };

  const handleSkuSearch = (skuCode: string) => {
    setScanSku(skuCode);
    const found = items.find(i => i.sku.toLowerCase() === skuCode.toLowerCase());
    if (found) {
      setScannedItem(found);
      setError('');
    } else {
      setScannedItem(null);
      if (skuCode) {
        setError('SKU not found in current inventory database.');
      }
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 p-8 rounded-3xl shadow-xl text-white">
          <div className="space-y-2">
            <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
              <FaBoxes className="text-amber-300 drop-shadow animate-pulse" />
              Inventory & Valuation Hub
            </h1>
            <p className="text-indigo-100/90 font-medium max-w-xl">
              Manage warehouses, adjust stock, verify LIFO/FIFO valuations, and run low-stock reports from a central panel.
            </p>
          </div>

          {/* Company selector */}
          {role !== 'company' && (
            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl max-w-sm space-y-2">
              <label className="block text-xs uppercase font-extrabold text-indigo-200 tracking-wider">Select Working Company</label>
              <select
                className="w-full bg-slate-900/40 border border-indigo-200/30 rounded-xl px-3 py-2 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-amber-300"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
              >
                <option value="" className="text-slate-950">-- Select Company --</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id} className="text-slate-950">{c.company_name || c.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Global Notifications */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl text-red-800 font-medium shadow-sm animate-fade-in">
            ⚠️ {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-xl text-green-800 font-medium shadow-sm animate-fade-in">
            <span className="inline-flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              {success}
            </span>
          </div>
        )}

        {!selectedCompanyId ? (
          <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-12 text-center text-slate-500 font-semibold">
            Please select a company above to view and manage inventory.
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-8 items-start">
            
            {/* Left Sidebar Menu */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-lg p-6 space-y-2">
              <h3 className="text-xs uppercase font-extrabold text-slate-400 tracking-wider px-3 mb-4">Navigation</h3>
              <button
                onClick={() => { setActiveTab('items'); stopCamera(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold transition ${activeTab === 'items' ? 'bg-indigo-50 text-indigo-700 shadow-inner' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FaBoxes className="text-lg" />
                Inventory Items
              </button>
              <button
                onClick={() => { setActiveTab('adjustments'); stopCamera(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold transition ${activeTab === 'adjustments' ? 'bg-indigo-50 text-indigo-700 shadow-inner' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FaExchangeAlt className="text-lg" />
                Ledger Adjustments
              </button>
              <button
                onClick={() => { setActiveTab('valuation'); stopCamera(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold transition ${activeTab === 'valuation' ? 'bg-indigo-50 text-indigo-700 shadow-inner' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FaCalculator className="text-lg" />
                Stock Valuation
              </button>
              <button
                onClick={() => { setActiveTab('alerts'); stopCamera(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold transition ${activeTab === 'alerts' ? 'bg-indigo-50 text-indigo-700 shadow-inner' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FaHistory className="text-lg" />
                Low Stock Alerts
                {lowStockItems.length > 0 && (
                  <span className="ml-auto bg-rose-500 text-white text-xs px-2 py-0.5 rounded-full font-extrabold animate-bounce">
                    {lowStockItems.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => { setActiveTab('scan'); startCamera(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold transition ${activeTab === 'scan' ? 'bg-indigo-50 text-indigo-700 shadow-inner' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FaBarcode className="text-lg" />
                Barcode Scanner
              </button>
            </div>

            {/* Right Tab Content */}
        <div className="lg:col-span-3 bg-white rounded-3xl border border-slate-100 shadow-lg p-8 min-h-[500px]">
              
              {/* Tab 1: Inventory Items */}
              {activeTab === 'items' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center border-b pb-4">
                    <h2 className="text-2xl font-extrabold text-slate-800">Inventory Catalog</h2>
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setItemForm({
                          sku: '',
                          name: '',
                          description: '',
                          price: '',
                          minimum_stock_level: '5',
                          packaging_category_id: '',
                          units_per_package: '1',
                          package_unit: 'pcs',
                          sku_abbreviation: '',
                          sku_type: 'Standard',
                          barcode: '',
                          batch_code: ''
                        });
                        setShowItemModal(true);
                      }}
                      className="bg-indigo-600 text-white font-bold px-4 py-2.5 rounded-xl hover:bg-indigo-500 transition shadow flex items-center gap-2"
                    >
                      <FaPlus /> Add New Item
                    </button>
                  </div>

                  {loading ? (
                    <div className="text-center py-10 text-slate-500">Loading catalog items...</div>
                  ) : items.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      No items registered in the database. Add your first item using the button above.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b text-slate-400 text-xs font-bold uppercase tracking-wider">
                            <th className="pb-3">SKU / Barcode</th>
                            <th className="pb-3">Name</th>
                            <th className="pb-3">Packaging Type</th>
                            <th className="pb-3">Current Stock</th>
                            <th className="pb-3">Min Level</th>
                            <th className="pb-3">Unit Price</th>
                            <th className="pb-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y text-slate-700 font-medium">
                          {items.map(item => (
                            <tr key={item.id} className="hover:bg-slate-50/50">
                              <td className="py-4">
                                <span className="block text-indigo-600 font-bold font-mono">{item.sku}</span>
                                {item.sku_abbreviation && (
                                  <span className="block text-[10px] text-slate-500 font-bold">Abbr: {item.sku_abbreviation}</span>
                                )}
                                {item.sku_type && (
                                  <span className="inline-block bg-indigo-50 border border-indigo-100 text-indigo-700 text-[9px] px-1 py-0.5 rounded font-extrabold mt-1">
                                    {item.sku_type}
                                  </span>
                                )}
                              </td>
                              <td className="py-4">
                                <span className="block text-slate-800 font-bold">{item.name}</span>
                                {item.barcode && (
                                  <span className="block text-[10px] text-slate-600 font-mono">Barcode: {item.barcode}</span>
                                )}
                                {item.batch_code && (
                                  <span className="block text-[10px] text-slate-600 font-mono">Batch: {item.batch_code}</span>
                                )}
                                <span className="block text-xs text-slate-400 font-normal">{item.description}</span>
                              </td>
                              <td className="py-4">
                                {item.packaging_category_name ? (
                                  <span className="text-slate-800 font-bold bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1 text-xs">
                                    {item.packaging_category_name} ({item.units_per_package || 1} {item.package_unit || 'pcs'})
                                  </span>
                                ) : (
                                  <span className="text-slate-400 font-medium bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1 text-xs">
                                    Uncategorized
                                  </span>
                                )}
                              </td>
                              <td className="py-4">
                                {(() => {
                                  const stockVal = parseFloat(String(item.current_stock || 0));
                                  const minLevel = parseFloat(String(item.minimum_stock_level || 0));
                                  const isLow = stockVal <= minLevel;
                                  return (
                                    <span className={`px-2.5 py-1.5 rounded-lg text-xs font-extrabold border ${isLow ? 'bg-rose-50 text-rose-800 border-rose-200 shadow-sm animate-pulse' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                                      {stockVal} units
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="py-4">
                                <span className="bg-amber-50 text-amber-800 px-2 py-1 rounded-lg text-xs font-bold border border-amber-200">
                                  {item.minimum_stock_level} units
                                </span>
                              </td>
                              <td className="py-4 text-slate-900 font-bold">₹ {Number(item.price || 0).toFixed(2)}</td>
                              <td className="py-4 text-right space-x-2">
                                 <button
                                   onClick={() => {
                                     setBarcodeModalItem(item);
                                     setLabelQuantity(4);
                                     setCustomBatch(item.batch_code || '');
                                   }}
                                   className="text-emerald-600 hover:text-emerald-500 p-2"
                                   title="Print Barcode & Batch Labels"
                                 >
                                   <FaBarcode className="text-lg" />
                                 </button>
                                 <button
                                   onClick={() => handleEditClick(item)}
                                   className="text-blue-600 hover:text-blue-500 p-2"
                                   title="Edit"
                                 >
                                   <FaEdit className="text-lg" />
                                 </button>
                                 <button
                                   onClick={() => handleDeleteItem(item.id)}
                                   className="text-rose-600 hover:text-rose-500 p-2"
                                   title="Delete"
                                 >
                                   <FaTrash className="text-lg" />
                                 </button>
                               </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 2: Ledger Adjustments & Warehouse Transfer */}
              {activeTab === 'adjustments' && (
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <h2 className="text-2xl font-extrabold text-slate-800">Ledger Adjustments & Transfers</h2>
                    <p className="text-xs text-slate-400 mt-1">Record physical stock increases, reductions, or transfer stock between warehouse locations.</p>
                  </div>

                  <form onSubmit={handleAdjustSubmit} className="space-y-6 bg-slate-50/70 p-6 rounded-2xl border border-slate-100">
                    <div className="grid md:grid-cols-2 gap-6">
                      
                      <div>
                        <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Select Item</label>
                        <select
                          className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none"
                          value={adjustForm.itemId}
                          onChange={(e) => setAdjustForm({...adjustForm, itemId: e.target.value})}
                          required
                        >
                          <option value="">-- Choose Item --</option>
                          {items.map(i => (
                            <option key={i.id} value={i.id}>[{i.sku}] {i.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Adjustment Type</label>
                        <select
                          className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none"
                          value={adjustForm.type}
                          onChange={(e) => setAdjustForm({...adjustForm, type: e.target.value})}
                          required
                        >
                          <option value="IN">Inward (Stock Addition)</option>
                          <option value="OUT">Outward (Stock reduction/Loss)</option>
                          <option value="TRANSFER">Transfer between Warehouses</option>
                        </select>
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Source Warehouse</label>
                        <select
                          className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none"
                          value={adjustForm.warehouseId}
                          onChange={(e) => setAdjustForm({...adjustForm, warehouseId: e.target.value})}
                          required
                        >
                          <option value="">-- Choose Warehouse --</option>
                          {warehouses.map(w => (
                            <option key={w.id} value={w.id}>{w.name} ({w.location})</option>
                          ))}
                        </select>
                      </div>

                      {adjustForm.type === 'TRANSFER' ? (
                        <div>
                          <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Target Warehouse</label>
                          <select
                            className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none border-indigo-200"
                            value={adjustForm.targetWarehouseId}
                            onChange={(e) => setAdjustForm({...adjustForm, targetWarehouseId: e.target.value})}
                            required
                          >
                            <option value="">-- Choose Destination --</option>
                            {warehouses
                              .filter(w => String(w.id) !== adjustForm.warehouseId)
                              .map(w => (
                                <option key={w.id} value={w.id}>{w.name} ({w.location})</option>
                              ))}
                          </select>
                        </div>
                      ) : (
                        <div>
                          <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Unit Cost (₹)</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Optional unit cost for valuation"
                            className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none"
                            value={adjustForm.unitCost}
                            onChange={(e) => setAdjustForm({...adjustForm, unitCost: e.target.value})}
                          />
                        </div>
                      )}

                      <div>
                        <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Quantity</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="e.g. 10"
                          className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none"
                          value={adjustForm.quantity}
                          onChange={(e) => setAdjustForm({...adjustForm, quantity: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Batch / Series Number</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="e.g. BATCH-2026-001"
                            className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                            value={adjustForm.batchNumber}
                            onChange={(e) => setAdjustForm({...adjustForm, batchNumber: e.target.value})}
                          />
                          {adjustForm.type === 'IN' && (
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  const dateCode = new Date().toISOString().slice(2, 10).replace(/-/g, "");
                                  const rand = Math.floor(1000 + Math.random() * 9000);
                                  setAdjustForm({...adjustForm, batchNumber: `LOT-${dateCode}-${rand}`});
                                }}
                                className="bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold px-3 py-2 rounded-xl text-xs hover:bg-indigo-100 transition whitespace-nowrap"
                                title="Auto-generate Batch Lot Number"
                              >
                                Gen Batch
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const randSeq = 'SN-' + Math.floor(100000 + Math.random() * 900000);
                                  setAdjustForm({...adjustForm, batchNumber: randSeq});
                                }}
                                className="bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold px-3 py-2 rounded-xl text-xs hover:bg-emerald-100 transition whitespace-nowrap"
                                title="Auto-generate Serial Number"
                              >
                                Gen Serial
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 text-xs mb-2 uppercase">Remarks / Reason</label>
                      <input
                        type="text"
                        placeholder="e.g. Regular adjustment, physical recount"
                        className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-indigo-400 focus:outline-none"
                        value={adjustForm.remarks}
                        onChange={(e) => setAdjustForm({...adjustForm, remarks: e.target.value})}
                      />
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-2.5 rounded-xl shadow transition"
                      >
                        Submit Ledger Entry
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Tab 3: Stock Valuation Report (FIFO / LIFO) */}
              {activeTab === 'valuation' && (
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <h2 className="text-2xl font-extrabold text-slate-800">Inventory Valuation Summary</h2>
                    <p className="text-xs text-slate-400 mt-1">
                      Valuation calculated automatically using the default method configured in Settings.
                    </p>
                  </div>

                  {valuationData.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      No stock valuation reports available. Post some inward stock transactions to build valuation stats.
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-indigo-50 border border-indigo-100 p-6 rounded-2xl space-y-1">
                          <span className="text-slate-500 font-bold text-xs uppercase">Total Items Tracked</span>
                          <h4 className="text-2xl font-extrabold text-indigo-900">{valuationData.length} items</h4>
                        </div>
                        <div className="bg-violet-50 border border-violet-100 p-6 rounded-2xl space-y-1">
                          <span className="text-slate-500 font-bold text-xs uppercase">Total Units in Stock</span>
                          <h4 className="text-2xl font-extrabold text-violet-900">
                            {valuationData.reduce((acc, v) => acc + Number(v.total_quantity || 0), 0).toLocaleString()} units
                          </h4>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-2xl space-y-1">
                          <span className="text-slate-500 font-bold text-xs uppercase">Total Valuation Asset Value</span>
                          <h4 className="text-2xl font-extrabold text-emerald-900">
                            ₹ {valuationData.reduce((acc, v) => acc + Number(v.total_value || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </h4>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b text-slate-400 text-xs font-bold uppercase tracking-wider">
                              <th className="pb-3">SKU</th>
                              <th className="pb-3">Item Name</th>
                              <th className="pb-3">On Hand Quantity</th>
                              <th className="pb-3">Average Cost</th>
                              <th className="pb-3 text-right">Total Asset Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y text-slate-700 font-medium">
                            {valuationData.map(v => (
                              <tr key={v.sku} className="hover:bg-slate-50/50">
                                <td className="py-4 text-indigo-600 font-bold font-mono">{v.sku}</td>
                                <td className="py-4 text-slate-800 font-bold">{v.name}</td>
                                <td className="py-4 font-bold">{v.total_quantity} units</td>
                                <td className="py-4">₹ {Number(v.average_cost || 0).toFixed(2)}</td>
                                <td className="py-4 text-right text-emerald-700 font-extrabold">₹ {Number(v.total_value || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab 4: Low Stock Scanner Alerts */}
              {activeTab === 'alerts' && (
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <h2 className="text-2xl font-extrabold text-slate-800">Low Stock Alerts & Levels</h2>
                    <p className="text-xs text-rose-500 font-medium">Items listed below have current quantities at or below their configured minimum levels.</p>
                  </div>

                  {lowStockItems.length === 0 ? (
                    <div className="bg-emerald-50 border border-emerald-100 p-8 rounded-2xl text-center text-emerald-800 font-bold">
                      🎉 All inventory levels are healthy! No low stock alerts.
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {lowStockItems.map(item => {
                        const pct = Math.min(100, Math.max(0, (item.current_stock / item.minimum_stock_level) * 100));
                        return (
                          <div key={item.id} className="bg-white border-2 border-rose-100 p-6 rounded-2xl shadow-sm space-y-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-extrabold text-slate-800 text-lg">{item.name}</h4>
                                <span className="text-indigo-600 font-bold text-xs font-mono tracking-wider uppercase">{item.sku}</span>
                              </div>
                              <span className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1 rounded-xl text-xs font-bold uppercase">
                                Action Required
                              </span>
                            </div>

                            <div className="space-y-2">
                              <div className="flex justify-between text-xs font-bold text-slate-600">
                                <span>Current: {item.current_stock} units</span>
                                <span>Min: {item.minimum_stock_level} units</span>
                              </div>
                              {/* progress bar */}
                              <div className="w-full bg-slate-100 rounded-full h-3.5 overflow-hidden border">
                                <div 
                                  className="bg-rose-500 h-full rounded-full transition-all duration-300"
                                  style={{ width: `${pct}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tab 5: Camera Barcode Scan Layout */}
              {activeTab === 'scan' && (
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <h2 className="text-2xl font-extrabold text-slate-800">Barcode / SKU Scanner</h2>
                    <p className="text-xs text-slate-400 mt-1">Scan items with your camera or search SKU directly to inspect stock levels.</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 items-start">
                    
                    {/* Scanner camera mock / viewfinder */}
                    <div className="space-y-4">
                      <div className="relative bg-slate-900 aspect-video rounded-3xl overflow-hidden border-4 border-slate-950 shadow-xl flex items-center justify-center text-white">
                        
                        {cameraActive ? (
                          <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover"></video>
                        ) : (
                          <div className="text-center p-6 space-y-3">
                            <span className="text-5xl mx-auto text-slate-500 animate-bounce">📷</span>
                            <p className="text-slate-400 text-xs font-bold">Viewfinder Inactive</p>
                          </div>
                        )}

                        {/* Scanner overlay target box */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-48 h-24 border-2 border-amber-300 border-dashed rounded-lg bg-white/5 backdrop-blur-2xs flex items-center justify-center text-[10px] text-amber-200 uppercase font-extrabold tracking-widest shadow-[0_0_20px_rgba(251,191,36,0.3)]">
                            Align Barcode Here
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        {cameraActive ? (
                          <button
                            type="button"
                            onClick={stopCamera}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 rounded-xl transition"
                          >
                            Disable Camera
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={startCamera}
                            className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 rounded-xl shadow transition"
                          >
                            Enable Camera
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={simulateScan}
                          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-extrabold px-6 py-2.5 rounded-xl shadow transition flex items-center gap-2"
                        >
                          Simulate Scan
                        </button>
                      </div>
                    </div>

                    {/* Scanner Output inspection */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <label className="block text-slate-700 font-bold text-xs uppercase">Scan SKU Code</label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Enter SKU or scan barcode..."
                            className="w-full border-2 border-slate-200 rounded-xl pl-4 pr-4 py-3 focus:border-indigo-400 focus:outline-none font-semibold font-mono"
                            value={scanSku}
                            onChange={(e) => handleSkuSearch(e.target.value)}
                          />
                        </div>
                      </div>

                      {scannedItem ? (
                        <div className="bg-indigo-50/60 border border-indigo-100 rounded-3xl p-6 space-y-4 animate-fade-in">
                          <div>
                            <span className="text-xs uppercase font-extrabold text-indigo-500 tracking-wider">Scanned Result</span>
                            <h3 className="text-xl font-extrabold text-indigo-950 mt-1">{scannedItem.name}</h3>
                            <p className="text-xs text-indigo-900/60 font-medium font-mono">{scannedItem.sku}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-xl border border-indigo-200/50">
                              <span className="text-[10px] uppercase font-bold text-slate-400 block">Unit Price</span>
                              <span className="font-extrabold text-lg text-slate-800">₹ {Number(scannedItem.price || 0).toFixed(2)}</span>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-indigo-200/50">
                              <span className="text-[10px] uppercase font-bold text-slate-400 block">Minimum Level</span>
                              <span className="font-extrabold text-lg text-slate-800">{scannedItem.minimum_stock_level} units</span>
                            </div>
                          </div>

                          <p className="text-xs text-indigo-900/80 italic">{scannedItem.description || 'No item description available.'}</p>

                          {/* Integrated Inward/Outward adjustment form */}
                          <form onSubmit={handleScanAdjustSubmit} className="bg-white border border-indigo-100 rounded-2xl p-4 mt-4 space-y-4 shadow-sm">
                            <span className="block text-xs uppercase font-extrabold text-indigo-700 tracking-wider">Record Stock Adjustment</span>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Adjustment Type</label>
                                <select
                                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 focus:border-indigo-400 focus:outline-none text-xs font-semibold text-slate-800"
                                  value={scanAdjustForm.type}
                                  onChange={(e) => setScanAdjustForm({...scanAdjustForm, type: e.target.value})}
                                >
                                  <option value="IN">Inward (Stock IN)</option>
                                  <option value="OUT">Outward (Stock OUT)</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Warehouse</label>
                                <select
                                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 focus:border-indigo-400 focus:outline-none text-xs font-semibold text-slate-800"
                                  value={scanAdjustForm.warehouseId}
                                  onChange={(e) => setScanAdjustForm({...scanAdjustForm, warehouseId: e.target.value})}
                                  required
                                >
                                  <option value="">Select Warehouse</option>
                                  {warehouses.map(w => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Quantity</label>
                                <input
                                  type="number"
                                  step="any"
                                  placeholder="e.g. 20"
                                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 focus:border-indigo-400 focus:outline-none text-xs font-semibold text-slate-800"
                                  value={scanAdjustForm.quantity}
                                  onChange={(e) => setScanAdjustForm({...scanAdjustForm, quantity: e.target.value})}
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Batch / Serial</label>
                                <div className="flex gap-1.5">
                                  <input
                                    type="text"
                                    placeholder="e.g. LOT-A"
                                    className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 focus:border-indigo-400 focus:outline-none text-xs font-semibold text-slate-800"
                                    value={scanAdjustForm.batchNumber}
                                    onChange={(e) => setScanAdjustForm({...scanAdjustForm, batchNumber: e.target.value})}
                                  />
                                  {scanAdjustForm.type === 'IN' && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const dateCode = new Date().toISOString().slice(2, 10).replace(/-/g, "");
                                        const rand = Math.floor(1000 + Math.random() * 9000);
                                        setScanAdjustForm({...scanAdjustForm, batchNumber: `LOT-${dateCode}-${rand}`});
                                      }}
                                      className="bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold px-2 py-1 rounded-lg text-[9px] hover:bg-indigo-100 transition whitespace-nowrap"
                                    >
                                      Gen
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Remarks</label>
                              <input
                                type="text"
                                placeholder="Scan adjust notes"
                                className="w-full border border-slate-200 rounded-lg px-2 py-1.5 focus:border-indigo-400 focus:outline-none text-xs font-semibold text-slate-800"
                                value={scanAdjustForm.remarks}
                                onChange={(e) => setScanAdjustForm({...scanAdjustForm, remarks: e.target.value})}
                              />
                            </div>

                            <button
                              type="submit"
                              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 rounded-xl text-xs shadow transition"
                            >
                              Post Scanner Transaction
                            </button>
                          </form>
                        </div>
                      ) : (
                        <div className="border border-slate-200 border-dashed rounded-3xl p-8 text-center text-slate-400 font-semibold">
                          No scan results. Simulate a scan or type a valid SKU to inspect.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {/* Item Modal (Add/Edit) */}
      {showItemModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl p-8 space-y-6">
            <h3 className="text-xl font-extrabold text-slate-800">
              {editingItem ? 'Edit Inventory Item' : 'Register New Inventory Item'}
            </h3>

            <form onSubmit={handleItemSubmit} className="space-y-4">
              <div>
                <label className="block text-slate-700 font-bold text-xs uppercase mb-1">SKU / Unique Barcode</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ITEM-XYZ-01"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none"
                  value={itemForm.sku}
                  onChange={(e) => setItemForm({...itemForm, sku: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">SKU Abbreviation</label>
                  <input
                    type="text"
                    placeholder="e.g. PW-A"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                    value={itemForm.sku_abbreviation}
                    onChange={(e) => setItemForm({...itemForm, sku_abbreviation: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">SKU Type</label>
                  <select
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                    value={itemForm.sku_type}
                    onChange={(e) => setItemForm({...itemForm, sku_type: e.target.value})}
                  >
                    <option value="Standard">Standard</option>
                    <option value="Serialized">Serialized</option>
                    <option value="Batch-tracked">Batch-tracked</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Barcode</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. 890123456789"
                      className="flex-1 border-2 border-slate-200 rounded-xl px-3 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold text-sm"
                      value={itemForm.barcode}
                      onChange={(e) => setItemForm({...itemForm, barcode: e.target.value})}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const randomBarcode = 'BAR-' + Math.floor(100000000 + Math.random() * 900000000);
                        setItemForm({...itemForm, barcode: randomBarcode});
                      }}
                      className="bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold px-2.5 py-1 rounded-xl text-xs"
                      title="Generate random barcode"
                    >
                      Gen
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Batch Code</label>
                  <input
                    type="text"
                    placeholder="e.g. BATCH-2026-07"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                    value={itemForm.batch_code}
                    onChange={(e) => setItemForm({...itemForm, batch_code: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Item Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Premium Widget A"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({...itemForm, name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Description</label>
                <input
                  type="text"
                  placeholder="e.g. Dimensions, weight, material details"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none"
                  value={itemForm.description}
                  onChange={(e) => setItemForm({...itemForm, description: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Packaging Type / Category</label>
                <div className="flex gap-2">
                  <select
                    className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                    value={itemForm.packaging_category_id}
                    onChange={(e) => setItemForm({...itemForm, packaging_category_id: e.target.value})}
                  >
                    <option value="">-- Select Packaging Type --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowQuickCategory(!showQuickCategory)}
                    className="bg-indigo-50 border border-indigo-200 text-indigo-700 font-bold px-3 py-2 rounded-xl text-xs hover:bg-indigo-100 transition whitespace-nowrap"
                  >
                    {showQuickCategory ? "Hide Manager" : "Manage Categories"}
                  </button>
                </div>
              </div>

              {itemForm.packaging_category_id && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl animate-fade-in">
                  <div>
                    <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Qty per Package</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 10"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                      value={itemForm.units_per_package}
                      onChange={(e) => setItemForm({...itemForm, units_per_package: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Unit Type (Measure)</label>
                    <select
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none text-slate-800 font-semibold"
                      value={itemForm.package_unit}
                      onChange={(e) => setItemForm({...itemForm, package_unit: e.target.value})}
                    >
                      <option value="pcs">pcs (Pieces)</option>
                      <option value="units">units</option>
                      <option value="nos">nos (Numbers)</option>
                      <option value="box">box</option>
                      <option value="pkt">pkt (Packets)</option>
                    </select>
                  </div>
                </div>
              )}

              {showQuickCategory && (
                <div className="bg-indigo-50/50 p-4 border border-indigo-100 rounded-2xl space-y-3 animate-fade-in">
                  <span className="block text-indigo-900 font-extrabold text-xs uppercase">Create Packaging Category Dynamically</span>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. Box of 10, Carton, Roll"
                      className="flex-1 border-2 border-indigo-200 rounded-xl px-3 py-1.5 focus:border-indigo-400 focus:outline-none text-sm font-semibold text-slate-800"
                      value={quickCategoryName}
                      onChange={(e) => setQuickCategoryName(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={handleQuickCategorySubmit}
                      className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-1.5 rounded-xl text-xs shadow transition"
                    >
                      Save
                    </button>
                  </div>

                  {categories.length > 0 && (
                    <div className="space-y-1.5 max-h-32 overflow-y-auto pt-2 border-t border-indigo-200/50">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block">Existing Categories (Click &times; to delete)</span>
                      <div className="flex flex-wrap gap-1.5">
                        {categories.map(cat => (
                          <span key={cat.id} className="inline-flex items-center gap-1 bg-white border border-indigo-100 rounded-lg px-2.5 py-1 text-xs text-slate-700 font-bold">
                            {cat.name}
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(cat.id)}
                              className="text-rose-500 hover:text-rose-700 font-extrabold ml-1 cursor-pointer"
                              title="Delete category"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Unit Price (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="e.g. 450.00"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({...itemForm, price: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Min Level Alert</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="e.g. 5"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none"
                    value={itemForm.minimum_stock_level}
                    onChange={(e) => setItemForm({...itemForm, minimum_stock_level: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowItemModal(false);
                    setEditingItem(null);
                    setItemForm({
                      sku: '',
                      name: '',
                      description: '',
                      price: '',
                      minimum_stock_level: '5',
                      packaging_category_id: '',
                      units_per_package: '1',
                      package_unit: 'pcs',
                      sku_abbreviation: '',
                      sku_type: 'Standard',
                      barcode: '',
                      batch_code: ''
                    });
                    setShowQuickCategory(false);
                    setQuickCategoryName('');
                  }}
                  className="flex-1 border-2 border-slate-200 text-slate-700 font-bold py-2 rounded-xl hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 rounded-xl shadow transition"
                >
                  Save Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Barcode & Batch Code Label Generator Modal */}
      {barcodeModalItem && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl p-8 space-y-6">
            <div className="flex justify-between items-center border-b pb-4">
              <h3 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                <FaBarcode className="text-indigo-600 text-2xl" /> Barcode & Batch Label Generator
              </h3>
              <button 
                onClick={() => setBarcodeModalItem(null)} 
                className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
              >
                &times;
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Configuration Controls */}
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 font-bold text-xs uppercase mb-1">Item Details</label>
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                    <span className="block text-slate-800 font-extrabold text-sm">{barcodeModalItem.name}</span>
                    <span className="block text-xs font-mono text-indigo-600 mt-1">SKU: {barcodeModalItem.sku}</span>
                    {barcodeModalItem.sku_abbreviation && (
                      <span className="block text-[10px] text-slate-500 font-bold">Abbr: {barcodeModalItem.sku_abbreviation}</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Print Quantity (Labels)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none font-bold text-slate-800"
                    value={labelQuantity}
                    onChange={(e) => setLabelQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-bold text-xs uppercase mb-1">Custom Batch Override (Optional)</label>
                  <input
                    type="text"
                    placeholder={barcodeModalItem.batch_code || 'e.g. LOT-A-1'}
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-indigo-400 focus:outline-none"
                    value={customBatch}
                    onChange={(e) => setCustomBatch(e.target.value)}
                  />
                </div>
              </div>

              {/* Label Preview */}
              <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-3xl p-6 bg-slate-50/50">
                <span className="text-slate-400 font-bold text-xs uppercase mb-3">Live Sticker Preview</span>
                
                {/* Visual Label representation */}
                <div 
                  className="bg-white border border-slate-300 rounded-xl p-4 shadow-sm w-[240px] min-h-[120px] flex flex-col justify-between"
                  style={{ fontFamily: 'monospace' }}
                >
                  <div className="flex justify-between items-center text-[8px] font-bold text-slate-500 border-b pb-1">
                    <span>ABBIEE PROJECTS</span>
                    <span>{barcodeModalItem.sku_abbreviation || barcodeModalItem.sku}</span>
                  </div>

                  <span className="block text-slate-800 font-bold text-[10px] truncate mt-1">
                    {barcodeModalItem.name}
                  </span>

                  {/* Simulated barcode graphic */}
                  <div className="my-2">
                    <div className="flex justify-center items-end gap-[1.5px] h-7 overflow-hidden">
                      {(() => {
                        const val = barcodeModalItem.barcode || '1234567890';
                        const bars = [];
                        for (let i = 0; i < 40; i++) {
                          const charCode = val.charCodeAt(i % val.length);
                          const w = ((charCode + i) % 3) + 1;
                          bars.push(
                            <div 
                              key={i} 
                              className="bg-black" 
                              style={{ width: `${w}px`, height: '100%' }} 
                            />
                          );
                        }
                        return bars;
                      })()}
                    </div>
                    <span className="block text-center text-[9px] font-bold tracking-wider mt-1">
                      {barcodeModalItem.barcode || 'NO BARCODE'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-[8px] font-bold border-t pt-1 text-slate-600">
                    <span>Batch: {customBatch || barcodeModalItem.batch_code || 'N/A'}</span>
                    <span className="text-slate-800">MRP: ₹{Number(barcodeModalItem.price || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hidden printable element container */}
            <div id="printable-labels-area" className="hidden">
              {Array.from({ length: labelQuantity }).map((_, i) => (
                <div key={i} className="label-card">
                  <div className="label-header">
                    <span>ABBIEE PROJECTS</span>
                    <span>{barcodeModalItem.sku_abbreviation || barcodeModalItem.sku}</span>
                  </div>
                  <div className="label-title">{barcodeModalItem.name}</div>
                  
                  <div className="barcode-sim">
                    {(() => {
                      const val = barcodeModalItem.barcode || '1234567890';
                      const bars = [];
                      for (let j = 0; j < 40; j++) {
                        const charCode = val.charCodeAt(j % val.length);
                        const w = ((charCode + j) % 3) + 1;
                        bars.push(
                          <div 
                            key={j} 
                            className="barcode-bar" 
                            style={{ width: `${w}px` }} 
                          />
                        );
                      }
                      return bars;
                    })()}
                  </div>
                  <div className="barcode-text">{barcodeModalItem.barcode || 'NO BARCODE'}</div>

                  <div className="label-footer">
                    <span>Batch: {customBatch || barcodeModalItem.batch_code || 'N/A'}</span>
                    <span>MRP: ₹{Number(barcodeModalItem.price || 0).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => setBarcodeModalItem(null)}
                className="flex-1 border-2 border-slate-200 text-slate-700 font-bold py-2.5 rounded-xl hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePrintLabels}
                className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 rounded-xl shadow transition flex items-center justify-center gap-2"
              >
                <FaBarcode /> Print {labelQuantity} Labels
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InventoryDashboard;
